import { memo } from 'react';
import { clsx } from 'clsx';
import { FormattedMessage } from 'react-intl';
import { VStack, Text, HStack, ErrorAlertBanner } from '@library';
import Divider from '../../../core/components/Divider';
import type {
    LoggedInProductCustomerPrice,
    Product,
    ProductItemOrVariant,
    ProductVariant,
} from '../../types';
import useLoginState from '../../../core/hooks/useLoginState';
import useCustomerPriceVisibility from '../../../core/hooks/useCustomerPriceVisibility';
import ProductRetailPrice from '../ProductTileView/ProductRetailPrice';
import ProductMyHEKPrice from '../ProductTileView/ProductMyHEKPrice';
import useProductQuantity from '../../queries/useProductQuantity';
import Button from '../../../core/components/Button';
import useStoreAndLanguageParams from '../../../core/hooks/useStoreAndLanguageParams';
import RestrictedContent from '../../../core/components/router/RestrictedContent';
import { useProductPrice } from '../../../pricing/queries/useProductPrice';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import useProductBackorders from '../../queries/useProductBackorders';
import useBrands from '../../../brand/queries/useBrands';
import ProductBackorderAccordion from '../ProductBackorders/ProductBackorderAccordion';
import ProductQuantityControl from '../../../core/components/ProductQuantityControl';
import ProductAvailability from './ProductAvailability';
import ProductAdditionalCharges from './ProductAdditionalCharges';
import styles from './ProductDetailsPricing.module.scss';

interface ProductDetailsPricingProps {
    disabledActions?: boolean;
    hasVariants?: boolean;
    isSummary?: boolean;
    sku?: string;
    fixedQuantity?: number;
    variant?: ProductVariant;
    onSale?: boolean;
    product: Product;
    replacementProducts: ProductItemOrVariant[];
}

const ProductDetailsAvailability = memo(
    ({
        isSummary = false,
        variant,
        hasVariants,
        desiredQuantity,
        sku,
        replacementProducts,
    }: {
        isSummary?: boolean;
        variant?: ProductVariant;
        hasVariants: boolean;
        desiredQuantity?: number;
        sku?: string;
        replacementProducts: ProductItemOrVariant[];
    }) => {
        if (isSummary) {
            return null;
        }

        return (
            <ProductAvailability
                variant={variant}
                hasVariants={hasVariants}
                desiredQuantity={desiredQuantity || 1}
                sku={sku}
                replacementProducts={replacementProducts}
            />
        );
    }
);

/* eslint complexity: ['error', 20] */
const ProductDetailsPricing = ({
    disabledActions = false,
    hasVariants = false,
    isSummary = false,
    fixedQuantity,
    sku,
    variant,
    onSale = false,
    product,
    replacementProducts,
}: ProductDetailsPricingProps) => {
    const isLoggedIn = useLoginState();
    const [showCustomerPrice] = useCustomerPriceVisibility();
    const { storeRootPath } = useStoreAndLanguageParams();
    const activeSku = hasVariants ? variant?.sku : sku;
    const {
        quantity,
        updateQuantity,
        loading: updateLoading,
        maxOrderQuantity,
        maxOrderQuantityLoading,
    } = useProductQuantity({ sku: activeSku });

    const { brands: brandsWithKeyDealers } = useBrands({
        withKeyDealers: true,
    });

    const {
        loading,
        startingPrice: variantStartingPrice,
        customerPrice,
    } = useProductPrice({
        product: product,
        selectedSku: activeSku,
    });

    const shouldShowDealerSearchButton =
        !isLoggedIn &&
        !!brandsWithKeyDealers.find(item => item.name === product.manufacturer);

    const { data: allBackorders } = useProductBackorders(
        hasVariants ? product.variants.map(item => item.sku) : [product.sku!]
    );

    const backorders = allBackorders?.filter(
        backorderItem => backorderItem.sku === activeSku
    );

    const hasPriceInfo = customerPrice && customerPrice.retailPrice > 0;

    return (
        <VStack gap="micro">
            <div className={styles.productDetailsSidebarSection}>
                <ProductDetailsAvailability
                    isSummary={isSummary}
                    variant={variant}
                    hasVariants={hasVariants}
                    desiredQuantity={fixedQuantity || quantity}
                    sku={activeSku}
                    replacementProducts={replacementProducts}
                />
            </div>
            <div
                className={clsx(styles.productDetailsSidebarSection, {
                    [styles.productDetailsSidebarPricingSection]:
                        hasPriceInfo || loading,
                })}
            >
                {hasPriceInfo || loading ? (
                    <>
                        <VStack gap="small">
                            <VStack>
                                <div className={styles.pricingItemContainer}>
                                    <ProductRetailPrice
                                        loading={loading}
                                        retailPrice={customerPrice?.retailPrice}
                                        isStartingPrice={variantStartingPrice}
                                    />
                                </div>
                                {showCustomerPrice && (
                                    <>
                                        <div
                                            className={
                                                styles.pricingItemsDivider
                                            }
                                        >
                                            <Divider lengthPercentage={100} />
                                        </div>
                                        <div
                                            className={
                                                styles.pricingItemContainer
                                            }
                                        >
                                            <ProductMyHEKPrice
                                                loading={loading}
                                                pricingInfo={
                                                    customerPrice as
                                                        | LoggedInProductCustomerPrice
                                                        | undefined
                                                }
                                                isStartingPrice={
                                                    variantStartingPrice
                                                }
                                                onSale={onSale}
                                                productWithAdditionalCharges={
                                                    product
                                                }
                                                product={
                                                    product.variants.find(
                                                        item =>
                                                            item.sku ===
                                                            activeSku
                                                    ) || product
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                            </VStack>

                            <HStack
                                gap="small"
                                align="center"
                                justify="spaceBetween"
                                data-test-class="ProductDetailsActions"
                            >
                                <ProductAdditionalCharges
                                    quantity={quantity}
                                    additionalCharges={
                                        variant?.additionalCharges ??
                                        product.additionalCharges
                                    }
                                />
                                {!isSummary && !disabledActions && (
                                    <RestrictedContent
                                        storeFlags={
                                            isLoggedIn
                                                ? undefined
                                                : 'cartSharingEnabled'
                                        }
                                    >
                                        <ProductQuantityControl
                                            small
                                            showErrorMessage
                                            id="ProductDetailsActionsQuantityInput"
                                            disabled={!activeSku}
                                            max={
                                                activeSku ? maxOrderQuantity : 0
                                            }
                                            value={quantity}
                                            loading={updateLoading}
                                            onChange={updateQuantity}
                                            maxQuantityLoading={
                                                maxOrderQuantityLoading
                                            }
                                            customQuantityChangeMessage={
                                                !activeSku ? (
                                                    <FormattedMessage id="please choose a size" />
                                                ) : undefined
                                            }
                                        />
                                    </RestrictedContent>
                                )}
                            </HStack>
                        </VStack>
                    </>
                ) : (
                    <ErrorAlertBanner noBorder>
                        <Text secondary>
                            <FormattedMessage id="Price loading error" />
                        </Text>
                    </ErrorAlertBanner>
                )}
            </div>
            <RestrictedContent storeFlags="dealerSearchEnabled">
                <div className={styles.findDealer}>
                    {shouldShowDealerSearchButton && (
                        <Button
                            autoSize
                            secondary
                            linkTo={`${storeRootPath}/dealers?brands=${product.manufacturer}`}
                        >
                            <FormattedMessage id="find dealers" />
                        </Button>
                    )}
                </div>
            </RestrictedContent>
            {backorders && backorders.length > 0 && (
                <ProductBackorderAccordion backorders={backorders} />
            )}
        </VStack>
    );
};

export default memo(withErrorBoundary(ProductDetailsPricing));
